// src/features/task/TaskWatchers.tsx
// Watchers panel for the task detail rail: watch/unwatch self, and add/remove
// any watcher via UserPicker. Wraps AddTaskWatcher / RemoveTaskWatcher.

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n, useAuth } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Alert,
} from '@react/components';
import { UserPicker } from '@/components/UserPicker';
import { getDisplayName, getInitials, getAvatarColor } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';
import type { Actor } from '@core/types/actor';

interface TaskWatchersProps {
  taskId: string;
  watchers: Actor[];
  editable?: boolean;
  onChanged?: () => void;
}

export const TaskWatchers = observer(({ taskId, watchers, editable = true, onChanged }: TaskWatchersProps) => {
  const store = useTask();
  const auth = useAuth();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const currentUserId = auth.activeProfileId || '';
  const isWatching = !!currentUserId && watchers.some((w) => w.entity?.id === currentUserId);

  const addWatcher = async (userId: string, displayName: string) => {
    if (!userId || watchers.some((w) => w.entity?.id === userId)) return;
    setBusyId(userId);
    setError(null);
    try {
      await store.AddTaskWatcher(taskId, {
        task: { id: taskId },
        watcher: { entity: { id: userId }, display_name: displayName || undefined },
      } as any);
      setAddOpen(false);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.watchControl.addError')));
    } finally {
      setBusyId(null);
    }
  };

  const removeWatcher = async (userId: string) => {
    setBusyId(userId);
    setError(null);
    try {
      await store.RemoveTaskWatcher(taskId, {
        task: { id: taskId },
        watcher: { entity: { id: userId } },
      } as any);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.watchControl.removeError')));
    } finally {
      setBusyId(null);
    }
  };

  const toggleSelfWatch = () => {
    if (!currentUserId) return;
    const name = auth.user?.username || auth.user?.email || t('task.watching');
    if (isWatching) removeWatcher(currentUserId);
    else addWatcher(currentUserId, name);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('task.watchers')}
          </p>
          {editable && currentUserId && (
            <Button
              size="sm"
              variant={isWatching ? 'secondary' : 'ghost'}
              isLoading={busyId === currentUserId}
              onClick={toggleSelfWatch}
            >
              {isWatching ? t('task.unwatch') : t('task.watch')}
            </Button>
          )}
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {watchers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.watchControl.empty')}</p>
        ) : (
          <div className="space-y-1.5">
            {watchers.map((w, idx) => {
              const wid = w.entity?.id || '';
              const name = getDisplayName(w);
              return (
                <div key={wid || idx} className="flex items-center justify-between gap-2 group">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white shrink-0"
                      style={{ backgroundColor: getAvatarColor(name) }}
                    >
                      {getInitials(name)}
                    </div>
                    <span className="text-sm text-foreground truncate">{name}</span>
                  </div>
                  {editable && wid && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive shrink-0 opacity-0 group-hover:opacity-100"
                      isLoading={busyId === wid}
                      onClick={() => removeWatcher(wid)}
                    >
                      {t('common.remove')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {editable && (
          addOpen ? (
            <div className="space-y-2 rounded-md border border-border p-2">
              <UserPicker
                value=""
                onChange={addWatcher}
                placeholder={t('task.addWatcher')}
              />
              <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>
              {t('task.addWatcher')}
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
});

export default TaskWatchers;
